import React from 'react';
import { BookOpen, TrendingUp, Users, Info, ChevronRight } from 'lucide-react';
import { tuitionFeeData } from '@/constants/tuitionFeeData';
import { useLocalizedPath } from '@/i18n/navigation';
import { useI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

const GROUPS = [
  { key: 'primary' as const, label: 'Primary School', rateClass: 'text-accent-700', cardBg: 'bg-accent-50/50' },
  { key: 'secondary' as const, label: 'Secondary School', rateClass: 'text-secondary', cardBg: 'bg-secondary-50/50' },
  { key: 'pre-university' as const, label: 'Pre-University', rateClass: 'text-primary', cardBg: 'bg-primary-50/50' },
];

const TUTOR_TIPS = [
  'Research market rates in your area before setting your price',
  'Be transparent about your qualifications and experience',
  'Consider long-term commitment discounts for recurring students',
  'Adjust rates based on travel distance for physical tuition',
];

const PARENT_TIPS = [
  'Compare rates from multiple tutors before deciding',
  'Weigh qualifications and track record, not just price',
  'Discuss payment terms and schedules upfront',
];

const TuitionFeeReferencePage: React.FC = () => {
  const localizePath = useLocalizedPath();
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  const allRates = tuitionFeeData.flatMap((d) => {
    const nums = d.averageRate.replace(/RM\s*/g, '').split(' - ').map(Number);
    return nums;
  });
  const minRate = Math.min(...allRates);
  const maxRate = Math.max(...allRates);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white pt-20 pb-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            {isZh ? '补习收费参考' : 'Tuition Rate Guide'}
          </h1>
          <p className="text-lg text-white/75 max-w-xl mx-auto leading-relaxed">
            {isZh
              ? '透明且更新的市场收费参考，帮助导师合理定价，也让家长更安心规划预算。'
              : 'Transparent, up-to-date market rates to help tutors price fairly and parents budget confidently.'}
          </p>

          {/* Quick stats */}
          <div className="mt-10 inline-flex items-center gap-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 text-sm">
            <div className="text-center">
              <p className="text-white/60 text-xs mb-0.5">{isZh ? '涵盖程度' : 'Levels Covered'}</p>
              <p className="font-bold text-lg">{GROUPS.length}</p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="text-center">
              <p className="text-white/60 text-xs mb-0.5">{isZh ? '收费范围' : 'Rate Range'}</p>
              <p className="font-bold text-lg">RM {minRate} – {maxRate}/hr</p>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="text-center">
              <p className="text-white/60 text-xs mb-0.5">{isZh ? '货币' : 'Currency'}</p>
              <p className="font-bold text-lg">MYR</p>
            </div>
          </div>
        </div>
      </section>

      {/* Rate Table */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{isZh ? '市场常见收费' : 'Standard Market Rates'}</h2>
            <p className="text-gray-500 text-sm">{isZh ? '每小时 · 一对一私人补习' : 'Per hour · One-on-one private tuition'}</p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="px-6 py-4 text-left text-sm font-semibold">{isZh ? '教育程度' : 'Education Level'}</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold">{isZh ? '平均收费 (RM/小时)' : 'Average Rate (RM/hr)'}</th>
                </tr>
              </thead>
              <tbody>
                {GROUPS.map((group) => {
                  const items = tuitionFeeData.filter((item) => item.group === group.key);
                  if (!items.length) return null;
                  return (
                    <React.Fragment key={group.key}>
                      <tr className={`${group.cardBg} border-t border-gray-200`}>
                        <td colSpan={2} className="px-6 py-4">
                          <p className={`text-base font-bold ${group.rateClass}`}>{localizeText(locale, group.label)}</p>
                        </td>
                      </tr>
                      {items.map((item, i) => (
                        <tr
                          key={i}
                          className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-gray-700 pl-8">{localizeText(locale, item.level)}</td>
                          <td className={`px-6 py-4 text-sm font-bold text-right ${group.rateClass}`}>
                            {item.averageRate}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 space-y-1">
            <p className="text-xs text-gray-400">{isZh ? '* 以上收费以马来西亚令吉 (RM) 按小时计算' : '* Rates quoted in Malaysian Ringgit (RM) per hour'}</p>
            <p className="text-xs text-gray-400">{isZh ? '* 小组补习一般按每位学生计算会低 30–50%' : '* Group tuition is typically 30–50% lower per student'}</p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-4 bg-white border border-primary-100 rounded-2xl p-5">
            <div className="w-9 h-9 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
              <Info className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800 mb-1">{isZh ? '以上收费仅供参考' : 'Rates are for reference only'}</p>
              <p className="text-sm text-gray-500 leading-relaxed">
                {isZh
                  ? '这些数字反映的是马来西亚补习市场的常见区间。实际收费会因导师资历、教学经验、科目难度、地点和交通要求而有所不同。我们建议双方沟通并达成最能体现教学价值的收费安排。'
                  : 'These figures represent typical ranges in the Malaysian tuition market. Actual rates may vary based on tutor qualifications, years of experience, subject complexity, location, and travel requirements. We encourage both parties to discuss and agree on rates that reflect the value provided.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{isZh ? '合理定价建议' : 'Tips for Fair Pricing'}</h2>
            <p className="text-gray-500 text-sm">{isZh ? '给导师与家长的参考建议' : 'Guidance for both tutors and parents'}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Tutor tips */}
            <div className="bg-secondary-50 rounded-2xl p-6 border border-secondary-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
                <h3 className="font-semibold text-secondary text-sm">{isZh ? '给导师' : 'For Tutors'}</h3>
              </div>
              <ul className="space-y-3">
                {TUTOR_TIPS.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      {isZh
                        ? [
                            '定价前先了解你所在地区的市场收费。',
                            '清楚说明你的资历与教学经验。',
                            '可为长期稳定上课的学生考虑承诺优惠。',
                            '线下补习可按交通距离调整收费。',
                          ][i]
                        : tip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Parent tips */}
            <div className="bg-primary-50 rounded-2xl p-6 border border-primary-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <h3 className="font-semibold text-primary text-sm">{isZh ? '给家长' : 'For Parents'}</h3>
              </div>
              <ul className="space-y-3">
                {PARENT_TIPS.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      {isZh
                        ? [
                            '决定前可先比较多位导师的收费。',
                            '除了价格，也应衡量资历与过往表现。',
                            '在开始前先确认付款方式与上课安排。',
                          ][i]
                        : tip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mx-auto mb-5">
            <BookOpen className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{isZh ? '准备好寻找导师了吗？' : 'Ready to find your tutor?'}</h3>
          <p className="text-gray-500 text-sm mb-6">{isZh ? '浏览不同程度与科目的认证导师。' : 'Browse verified tutors across all levels and subjects.'}</p>
          <a
            href={localizePath('/tutors')}
            className="inline-flex items-center gap-2 btn-primary text-sm px-6 py-3 rounded-xl font-medium"
          >
            {isZh ? '浏览导师' : 'Browse Tutors'}
            <ChevronRight className="h-4 w-4" />
          </a>
        </div>
      </section>

    </div>
  );
};

export default TuitionFeeReferencePage;
